import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { availabilityService, practiceService, dateTimeHelpers } from '../models/database';
import Header from '../components/Header';
import Footer from '../components/Footer';

// Import components
import PracticeDetails from '../components/availability/PracticeDetails';
import AppointmentSlots from '../components/availability/AppointmentSlots';
import SuccessView from '../components/availability/SuccessView';
import NoAppointmentsView from '../components/availability/NoAppointmentsView';
import NoAppointmentsForDateView from '../components/availability/NoAppointmentsForDateView';
import UserInfo from '../components/availability/UserInfo';
import AppointmentDate from '../components/availability/AppointmentDate';
import LoadingState from '../components/availability/LoadingState';
import LocationDebug from '../components/debug/LocationDebug';
import PaymentProcessing from '../components/availability/PaymentProcessing';

// Import utilities and services
import { formatTime, formatDate, getTodayDateString } from '../utils/appointmentUtils';
import { findNextAvailableAppointment, sortPracticesByDistance } from '../utils/locationUtils';
import { getAppointmentsForDateAndPractice } from '../services/appointmentService';

const AvailabilityCheckPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [nextAvailableDates, setNextAvailableDates] = useState({});
  const [success, setSuccess] = useState(false);
  const [searchDistance, setSearchDistance] = useState(20); // Default search distance in km
  const [noAppointmentsWithinDistance, setNoAppointmentsWithinDistance] = useState(false);
  const [allPractices, setAllPractices] = useState([]);
  const [showPaymentScreen, setShowPaymentScreen] = useState(false);
  
  // Get data from location state
  const formData = location.state?.formData || null;
  const [nearestPractice, setNearestPractice] = useState(location.state?.nearestPractice || null);
  const [sortedPractices, setSortedPractices] = useState(Array.isArray(location.state?.sortedPractices) ? location.state?.sortedPractices : []);
  
  // If no data was passed, redirect to home
  useEffect(() => {
    if (!formData || !nearestPractice) {
      navigate('/');
    }
  }, [formData, nearestPractice, navigate]);
  
  // Fetch all practices on component mount
  useEffect(() => {
    const fetchPractices = async () => {
      try {
        const practicesData = await practiceService.getAllPractices();
        setAllPractices(practicesData);
      } catch (err) {
        console.error('Error fetching practices:', err);
      }
    };
    
    fetchPractices();
  }, []);
  
  // Find next available appointment on component mount
  useEffect(() => {
    if (formData?.postcode) {
      findNextAvailableAppointmentWithinDistance();
    }
  }, [formData?.postcode]); // Add dependency to avoid lint warnings
  
  // Check for appointments within the next 7 days for a practice
  const checkAppointmentsForNext7Days = async (practiceId) => {
    console.log(`Checking appointments for next 7 days for practice ${practiceId}`);
    
    // Check next 7 days
    for (let i = 0; i < 7; i++) {
      const checkDate = new Date();
      checkDate.setDate(checkDate.getDate() + i);
      
      const year = checkDate.getFullYear();
      const month = (checkDate.getMonth() + 1).toString().padStart(2, '0');
      const day = checkDate.getDate().toString().padStart(2, '0');
      
      const dateString = `${year}-${month}-${day}`;
      
      console.log(`Checking date ${dateString} for practice ${practiceId}`);
      
      try {
        const slots = await checkAvailabilityForDate(practiceId, dateString);
        
        if (slots.length > 0) {
          console.log(`Found ${slots.length} slots for practice ${practiceId} on ${dateString}`);
          
          // Return the first available slot and date
          return {
            date: dateString,
            slot: slots[0]
          };
        }
      } catch (err) {
        console.error(`Error checking availability for practice ${practiceId} on date ${dateString}:`, err);
      }
    }
    
    // No appointments found within the next 7 days
    return null;
  };
  
  // Format date for display
  const formatDateFull = (dateString) => {
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    
    return date.toLocaleDateString('en-GB', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };
  
  // Check availability for a specific practice and date
  const checkAvailabilityForDate = async (practiceId, date) => {
    try {
      console.log(`Checking availability for practice ${practiceId} on date ${date}`);
      
      // Parse the date string (format: YYYY-MM-DD)
      const [year, month, day] = date.split('-').map(Number);
      
      // Create a date timestamp for the selected date using dateTimeHelpers
      const dateTimestamp = dateTimeHelpers.createDateTimestamp(year, month, day);
      
      // Get availability for the selected practice and date
      const availabilityData = await availabilityService.getPracticeAvailability(practiceId, dateTimestamp);
      
      console.log(`Received availability data for practice ${practiceId}:`, availabilityData);
      
      // Get existing appointments for this practice and date
      const existingAppointments = await getAppointmentsForDateAndPractice(practiceId, date);
      console.log(`Found ${existingAppointments.length} existing appointments for practice ${practiceId} on date ${date}`);
      
      // Create a map of booked slots for quick lookup
      const bookedSlots = new Map();
      existingAppointments.forEach(appointment => {
        if (appointment.slot && 
            appointment.slot.availabilityId && 
            appointment.slot.slotIndex !== undefined) {
          const key = `${appointment.slot.availabilityId}-${appointment.slot.slotIndex}`;
          bookedSlots.set(key, true);
          console.log(`Marking slot as booked: ${key}`);
        }
      });
      
      // Collect all available slots from all operatives
      const slots = [];
      
      // Check if the date is today
      const isToday = date === getTodayDateString();
      const now = new Date();
      
      if (availabilityData && Array.isArray(availabilityData)) {
        availabilityData.forEach(availability => {
          if (availability && availability.slots && Array.isArray(availability.slots)) {
            availability.slots.forEach((slot, index) => {
              // Check if the slot is already booked in the availability data
              const isMarkedAsBooked = slot.isBooked === true;
              
              // Check if the slot is booked in the appointments collection
              const slotKey = `${availability.id}-${index}`;
              const isBookedInAppointments = bookedSlots.has(slotKey);
              
              // Log the slot status for debugging
              console.log(`Slot ${index} in availability ${availability.id}:`);
              console.log(`  isMarkedAsBooked: ${isMarkedAsBooked}`);
              console.log(`  isBookedInAppointments: ${isBookedInAppointments}`);
              
              // Only include slots that are not booked in either the availability or appointments collection
              if (slot && !isMarkedAsBooked && !isBookedInAppointments) {
                // If the date is today, only include slots that haven't passed yet
                if (isToday) {
                  const slotTime = slot.startTime.toDate();
                  if (slotTime > now) {
                    slots.push({
                      id: `${availability.id}-${index}`,
                      operativeId: availability.operativeId,
                      startTime: slot.startTime,
                      endTime: slot.endTime,
                      availabilityId: availability.id,
                      slotIndex: index,
                      practiceId: practiceId
                    });
                  }
                } else {
                  // For future dates, include all slots
                  slots.push({
                    id: `${availability.id}-${index}`,
                    operativeId: availability.operativeId,
                    startTime: slot.startTime,
                    endTime: slot.endTime,
                    availabilityId: availability.id,
                    slotIndex: index,
                    practiceId: practiceId
                  });
                }
              }
            });
          }
        });
        
        // Sort slots by start time
        if (slots.length > 0) {
          slots.sort((a, b) => a.startTime.seconds - b.startTime.seconds);
        }
      }
      
      console.log(`Found ${slots.length} available slots for practice ${practiceId} on date ${date}`);
      return slots;
    } catch (err) {
      console.error(`Error checking availability for practice ${practiceId} on date ${date}:`, err);
      return [];
    }
  };
  
  // Find the next available appointment within the specified distance
  const findNextAvailableAppointmentWithinDistance = async () => {
    if (!formData.postcode) {
      return;
    }
    
    try {
      setLoading(true);
      setNoAppointmentsWithinDistance(false);
      setAvailableSlots([]);
      
      console.log(`Finding next available appointment within ${searchDistance}km of ${formData.postcode}`);
      
      // Get all practices or use existing ones
      const practices = allPractices.length > 0 
        ? allPractices 
        : await practiceService.getAllPractices();
      
      console.log(`Found ${practices.length} practices in total`);
      
      // Sort practices by distance
      const sorted = await sortPracticesByDistance(formData.postcode, practices);
      
      // Log all practices with their distances for debugging
      console.log('All practices with distances:', sorted.map(p => `${p.name}: ${p.distance}km`));
      
      // Filter practices within search distance (add a larger buffer to account for rounding)
      const practicesInRange = sorted.filter(p => p.distance <= searchDistance * 1.1); // Add 10% buffer
      
      console.log(`Found ${practicesInRange.length} practices within ${searchDistance}km`);
      console.log('Practices in range:', practicesInRange.map(p => `${p.name}: ${p.distance}km`));
      
      if (practicesInRange.length === 0) {
        setNoAppointmentsWithinDistance(true);
        setError(`No practices found within ${searchDistance}km of your location.`);
        setLoading(false);
        return;
      }
      
      // Check next 14 days for each practice
      const availabilityMap = {};
      let foundAvailability = false;
      let nearestPracticeWithAvailability = null;
      let nearestPracticeSlots = [];
      let nearestPracticeDate = null;
      
      // Check for Bromley Dental Studio with a future date
      const bromleyPractice = practicesInRange.find(p => p.name === "Bromley Dental Studio");
      if (bromleyPractice) {
        // Use a date 7 days from now instead of a hardcoded date
        const futureDate = new Date();
        futureDate.setDate(futureDate.getDate() + 7);
        const year = futureDate.getFullYear();
        const month = (futureDate.getMonth() + 1).toString().padStart(2, '0');
        const day = futureDate.getDate().toString().padStart(2, '0');
        const futureDateString = `${year}-${month}-${day}`;
        
        console.log(`Found Bromley Dental Studio, checking for ${futureDateString}`);
        const slots = await checkAvailabilityForDate(bromleyPractice.id, futureDateString);
        
        if (slots.length > 0) {
          console.log(`Found ${slots.length} slots for Bromley Dental Studio on ${futureDateString}`);
          
          // Store the first available date for this practice
          availabilityMap[bromleyPractice.id] = {
            date: futureDateString,
            slots
          };
          
          nearestPracticeWithAvailability = bromleyPractice;
          nearestPracticeSlots = slots;
          nearestPracticeDate = futureDateString;
          foundAvailability = true;
        } else {
          console.log(`No slots found for Bromley Dental Studio on ${futureDateString}`);
        }
      }
      
      // If we didn't find availability for Bromley, check all practices
      if (!foundAvailability) {
        // Check today specifically for each practice first
        const today = new Date();
        const todayYear = today.getFullYear();
        const todayMonth = today.getMonth() + 1; // JavaScript months are 0-indexed
        const todayDay = today.getDate();
        
        const todayString = `${todayYear}-${todayMonth.toString().padStart(2, '0')}-${todayDay.toString().padStart(2, '0')}`;
        
        console.log(`Checking today (${todayString}) for all practices first`);
        
        // First, check today for all practices
        for (const practice of practicesInRange) {
          console.log(`Checking practice ${practice.name} (${practice.id}) for today`);
          
          try {
            const slots = await checkAvailabilityForDate(practice.id, todayString);
            
            if (slots.length > 0) {
              console.log(`Found ${slots.length} slots for practice ${practice.name} today`);
              
              // Store the first available date for this practice
              availabilityMap[practice.id] = {
                date: todayString,
                slots
              };
              
              // If this is the first practice with availability or it's closer than the current nearest
              if (!nearestPracticeWithAvailability || practice.distance < nearestPracticeWithAvailability.distance) {
                nearestPracticeWithAvailability = practice;
                nearestPracticeSlots = slots;
                nearestPracticeDate = todayString;
              }
              
              foundAvailability = true;
            } else {
              console.log(`No slots found for practice ${practice.name} today`);
            }
          } catch (err) {
            console.error(`Error checking availability for practice ${practice.id} on date ${todayString}:`, err);
          }
        }
        
        // If no availability found today, check the next 13 days
        if (!foundAvailability) {
          console.log(`No availability found today, checking next 13 days`);
          
          // Check each practice for the next available appointment
          for (const practice of practicesInRange) {
            console.log(`Checking practice ${practice.name} (${practice.id}) for next 13 days`);
            
            // Check next 13 days (skipping today as we already checked it)
            for (let i = 1; i < 14; i++) {
              const checkDate = new Date();
              checkDate.setDate(checkDate.getDate() + i);
              
              const year = checkDate.getFullYear();
              const month = checkDate.getMonth() + 1; // JavaScript months are 0-indexed
              const day = checkDate.getDate();
              
              const dateString = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
              
              console.log(`Checking date ${dateString} for practice ${practice.name}`);
              
              try {
                const slots = await checkAvailabilityForDate(practice.id, dateString);
                
                if (slots.length > 0) {
                  console.log(`Found ${slots.length} slots for practice ${practice.name} on ${dateString}`);
                  
                  // Store the first available date for this practice
                  if (!availabilityMap[practice.id]) {
                    availabilityMap[practice.id] = {
                      date: dateString,
                      slots
                    };
                    
                    // If this is the first practice with availability or it's closer than the current nearest
                    if (!nearestPracticeWithAvailability || practice.distance < nearestPracticeWithAvailability.distance) {
                      nearestPracticeWithAvailability = practice;
                      nearestPracticeSlots = slots;
                      nearestPracticeDate = dateString;
                    }
                    
                    foundAvailability = true;
                  }
                  
                  // No need to check more dates for this practice
                  break;
                } else {
                  console.log(`No slots found for practice ${practice.name} on ${dateString}`);
                }
              } catch (err) {
                console.error(`Error checking availability for practice ${practice.id} on date ${dateString}:`, err);
              }
            }
          }
        }
      }
      
      // Check for appointments for all practices within range (for alternative options)
      console.log('Checking for appointments for all practices within range for alternative options');
      
      // Get the top 3 alternative practices (excluding the nearest one with availability)
      const alternativePractices = practicesInRange
        .filter(p => nearestPracticeWithAvailability && p.id !== nearestPracticeWithAvailability.id)
        .slice(0, 3);
      
      // Check for appointments for each alternative practice
      for (const practice of alternativePractices) {
        // Skip if we already have appointments for this practice
        if (availabilityMap[practice.id]) continue;
        
        console.log(`Checking for appointments for alternative practice ${practice.name}`);
        
        // Check for appointments within the next 7 days
        const result = await checkAppointmentsForNext7Days(practice.id);
        
        if (result) {
          // Found appointments
          const { date, slot } = result;
          const slots = await checkAvailabilityForDate(practice.id, date);
          
          // Store the appointments
          availabilityMap[practice.id] = {
            date,
            slots
          };
          
          console.log(`Found ${slots.length} slots for alternative practice ${practice.name} on ${date}`);
        } else {
          console.log(`No appointments found for alternative practice ${practice.name} within the next 7 days`);
        }
      }
      
      // Store all next available dates
      setNextAvailableDates(availabilityMap);
      
      console.log(`Found availability: ${foundAvailability}`);
      console.log(`Nearest practice with availability: ${nearestPracticeWithAvailability?.name || 'None'}`);
      
      if (foundAvailability && nearestPracticeWithAvailability) {
        // Found a practice with available slots
        console.log(`Setting nearest practice to ${nearestPracticeWithAvailability.name}`);
        setNearestPractice(nearestPracticeWithAvailability);
        setAvailableSlots(nearestPracticeSlots);
        setError(null);
        
        // Update the URL state with the new practice that has availability
        navigate('/availability-check', { 
          state: { 
            formData,
            nearestPractice: nearestPracticeWithAvailability,
            sortedPractices: Array.isArray(sorted) ? sorted : []
          },
          replace: true // Replace the current entry in the history stack
        });
      } else {
        // No available appointments within the distance
        console.log(`No available appointments found within ${searchDistance}km`);
        setNoAppointmentsWithinDistance(true);
        setAvailableSlots([]);
        setError(`No available appointments found within ${searchDistance}km. Try increasing the search distance.`);
      }
    } catch (err) {
      console.error('Error finding next available appointment:', err);
      setError('Failed to find available appointments. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Increase search distance and try again
  const increaseSearchDistance = () => {
    // Scroll to top of the page before searching with increased distance
    window.scrollTo(0, 0);
    
    const newDistance = searchDistance + 20; // Increase by 20km
    setSearchDistance(newDistance);
    setError(`Searching for appointments within ${newDistance}km...`);
    
    // Re-run the search with the new distance
    setTimeout(() => {
      findNextAvailableAppointmentWithinDistance();
    }, 100);
  };
  
  // Handle slot selection
  const handleSlotSelect = (slot) => {
    setSelectedSlot(slot);
  };
  
  // Refresh available slots to ensure we have the latest data
  const refreshAvailableSlots = async () => {
    if (!nearestPractice) return;
    
    setLoading(true);
    try {
      // Get the current date for the practice
      const currentDate = nearestPractice && nextAvailableDates[nearestPractice.id]?.date || getTodayDateString();
      
      // Fetch the latest availability data
      const slots = await checkAvailabilityForDate(nearestPractice.id, currentDate);
      
      // Update the state
      setAvailableSlots(slots);
      
      // If the selected slot is no longer available, clear the selection
      if (selectedSlot) {
        const slotStillAvailable = slots.some(slot => 
          slot.availabilityId === selectedSlot.availabilityId && 
          slot.slotIndex === selectedSlot.slotIndex
        );
        
        if (!slotStillAvailable) {
          setSelectedSlot(null);
          setError('The previously selected slot is no longer available. Please select another time.');
        }
      }
      
      // Update the nextAvailableDates state
      setNextAvailableDates(prev => ({
        ...prev,
        [nearestPractice.id]: {
          date: currentDate,
          slots
        }
      }));
      
    } catch (err) {
      console.error('Error refreshing available slots:', err);
      setError('Failed to refresh available slots. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle booking - proceed to payment screen
  const handleBooking = async () => {
    if (!selectedSlot) {
      setError('Please select an appointment slot.');
      return;
    }
    
    // Refresh available slots to make sure the selected slot is still available
    await refreshAvailableSlots();
    
    // Check if the selected slot is still available after refreshing
    const slotStillAvailable = availableSlots.some(slot => 
      slot.availabilityId === selectedSlot.availabilityId && 
      slot.slotIndex === selectedSlot.slotIndex
    );
    
    if (!slotStillAvailable) {
      setError('The selected slot is no longer available. Please select another time.');
      return;
    }
    
    // Scroll to top of the page before showing payment screen
    window.scrollTo(0, 0);
    
    // Show payment screen
    setShowPaymentScreen(true);
  };
  
  // Handle going back from payment screen
  const handleBackFromPayment = () => {
    // Scroll to top of the page before showing appointment selection screen
    window.scrollTo(0, 0);
    
    setShowPaymentScreen(false);
  };
  
  // Handle successful payment
  const handlePaymentSuccess = () => {
    // Scroll to top of the page before showing success screen
    window.scrollTo(0, 0);
    
    setSuccess(true);
    setShowPaymentScreen(false);
  };
  
  // Debug function to log the state of sortedPractices
  const debugSortedPractices = () => {
    console.log('sortedPractices:', sortedPractices);
    console.log('sortedPractices is array:', Array.isArray(sortedPractices));
    console.log('sortedPractices type:', typeof sortedPractices);
    
    if (!Array.isArray(sortedPractices)) {
      console.error('sortedPractices is not an array!', sortedPractices);
      // Force it to be an array
      setSortedPractices([]);
    }
  };
  
  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Debug logging
    console.log('AvailabilityCheckPage - handleSubmit called');
    console.log('AvailabilityCheckPage - sortedPractices:', sortedPractices);
    console.log('AvailabilityCheckPage - sortedPractices is array:', Array.isArray(sortedPractices));
    
    // Force sortedPractices to be an array if it's not
    if (!Array.isArray(sortedPractices)) {
      console.error('AvailabilityCheckPage - sortedPractices is not an array in handleSubmit!', sortedPractices);
      setSortedPractices([]);
      return; // Don't proceed with the form submission
    }
    
    // Continue with form submission
    console.log('AvailabilityCheckPage - handleSubmit proceeding with form submission');
  };
  
  // Handle return to home
  const handleReturnHome = () => {
    // Scroll to top of the page before navigating to home
    window.scrollTo(0, 0);
    
    navigate('/');
  };
  
  if (!formData || !nearestPractice) {
    return null; // Will redirect in useEffect
  }
  
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-grow py-12 bg-gray-50">
        {/* Debug component */}
        {formData?.postcode && <LocationDebug postcode={formData.postcode} />}
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
      {success ? (
        <SuccessView 
          formData={formData}
          practice={nearestPractice}
          selectedSlot={selectedSlot}
          nextAvailableDates={nextAvailableDates}
          formatDateFull={formatDateFull}
          formatTime={formatTime}
          getTodayDateString={getTodayDateString}
          onReturnHome={handleReturnHome}
        />
      ) : showPaymentScreen ? (
        <div className="bg-white p-6 rounded-lg shadow-lg mb-6">
          <h1 className="text-3xl font-bold text-primary font-heading mb-4">
            Payment Processing
          </h1>
          <PaymentProcessing
            formData={formData}
            practice={nearestPractice}
            selectedSlot={selectedSlot}
            selectedDate={nearestPractice && nextAvailableDates[nearestPractice.id]?.date || getTodayDateString()}
            onSuccess={handlePaymentSuccess}
            onBack={handleBackFromPayment}
            nextAvailableDates={nextAvailableDates}
          />
        </div>
      ) : (
        <>
                <div className="bg-white p-6 rounded-lg shadow-lg mb-6">
                  <h1 className="text-3xl font-bold text-primary font-heading mb-4">
                    Next Available Appointment
                  </h1>
                  
                  {noAppointmentsWithinDistance ? (
                    <NoAppointmentsView 
                      searchDistance={searchDistance} 
                      onIncreaseDistance={increaseSearchDistance} 
                    />
                  ) : availableSlots.length > 0 ? (
                    <PracticeDetails 
                      practice={nearestPractice} 
                      formData={formData} 
                      alternativeOptions={
                        Array.isArray(sortedPractices) && sortedPractices.length > 1
                          ? sortedPractices
                              .filter(p => p.id !== nearestPractice.id) // Exclude the current practice
                              .slice(0, 3) // Take up to 3 alternatives
                              .map(practice => {
                                // Check if we already have appointments for this practice
                                const hasAppointments = nextAvailableDates[practice.id]?.slots?.length > 0;
                                
                                return {
                                  practice,
                                  nextAppointment: nextAvailableDates[practice.id]?.slots[0] || null,
                                  onSelect: async () => {
                                    // Handle selecting an alternative practice
                                    setLoading(true);
                                    
                                    try {
                                      // If we already have appointments for this practice
                                      if (hasAppointments) {
                                        setNearestPractice(practice);
                                        setAvailableSlots(nextAvailableDates[practice.id].slots);
                                        setSelectedSlot(null);
                                      } else {
                                        // Check for appointments within the next 7 days
                                        const result = await checkAppointmentsForNext7Days(practice.id);
                                        
                                        if (result) {
                                          // Found appointments
                                          const { date, slot } = result;
                                          const slots = await checkAvailabilityForDate(practice.id, date);
                                          
                                          // Update the nextAvailableDates state
                                          setNextAvailableDates(prev => ({
                                            ...prev,
                                            [practice.id]: {
                                              date,
                                              slots
                                            }
                                          }));
                                          
                                          // Update the UI
                                          setNearestPractice(practice);
                                          setAvailableSlots(slots);
                                          setSelectedSlot(null);
                                        } else {
                                          // No appointments found
                                          setError(`No available appointments found for ${practice.name} within the next 7 days.`);
                                        }
                                      }
                                    } catch (err) {
                                      console.error('Error selecting alternative practice:', err);
                                      setError('Failed to check availability for the selected practice. Please try again.');
                                    } finally {
                                      setLoading(false);
                                    }
                                  }
                                };
                              })
                          : []
                      }
                    />
                  ) : loading ? (
                    <LoadingState />
                  ) : (
                    <div className="bg-gray-100 p-4 rounded border border-gray-300 mb-6">
                      <p className="text-gray-700 text-center">Finding the next available appointment...</p>
                    </div>
                  )}
                  
                  {/* Always show user info and date selector, even when no slots are available */}
                  {nearestPractice && (
                    <>
                      <UserInfo formData={formData} />
                      
                      <AppointmentDate 
                        practice={nearestPractice}
                        nextAvailableDates={nextAvailableDates}
                        formatDateFull={formatDateFull}
                        getTodayDateString={getTodayDateString}
                        onSelectDate={(date) => {
                          // Check if the selected date is in the past
                          const today = new Date();
                          today.setHours(0, 0, 0, 0); // Set to beginning of day for comparison
                          
                          const selectedDate = new Date(date);
                          selectedDate.setHours(0, 0, 0, 0); // Set to beginning of day for comparison
                          
                          if (selectedDate < today) {
                            // If the date is in the past, show an error message
                            setError('Please select a date that is not in the past.');
                            // Use today's date instead
                            const todayString = getTodayDateString();
                            
                            // Check availability for today
                            const checkAvailabilityForToday = async () => {
                              // Scroll to top of the page when checking today's availability
                              window.scrollTo(0, 0);
                              
                              setLoading(true);
                              try {
                                const slots = await checkAvailabilityForDate(nearestPractice.id, todayString);
                                setAvailableSlots(slots);
                                setSelectedSlot(null);
                                
                                if (slots.length === 0) {
                                  setError('No appointments available today. Please select a future date.');
                                }
                              } catch (err) {
                                console.error(`Error checking availability for today:`, err);
                                setError(`Failed to check availability for today. Please try again.`);
                              } finally {
                                setLoading(false);
                              }
                            };
                            
                            checkAvailabilityForToday();
                            return;
                          }
                          
                          // Check availability for the selected date
                          const checkAvailabilityForSelectedDate = async () => {
                            // Scroll to top of the page when selecting a new date
                            window.scrollTo(0, 0);
                            
                            setLoading(true);
                            try {
                              const slots = await checkAvailabilityForDate(nearestPractice.id, date);
                              setAvailableSlots(slots);
                              setSelectedSlot(null);
                              
                              if (slots.length === 0) {
                                setError(`No appointments available on ${formatDateFull(date)}. Please select another date.`);
                              } else {
                                setError(null);
                              }
                            } catch (err) {
                              console.error(`Error checking availability for date ${date}:`, err);
                              setError(`Failed to check availability for the selected date. Please try again.`);
                            } finally {
                              setLoading(false);
                            }
                          };
                          
                          checkAvailabilityForSelectedDate();
                        }}
                      />
                      
                      {availableSlots.length > 0 ? (
                        <AppointmentSlots 
                          slots={availableSlots}
                          selectedSlot={selectedSlot}
                          onSelectSlot={handleSlotSelect}
                          formatTime={formatTime}
                        />
                      ) : (
                        <NoAppointmentsForDateView 
                          date={nearestPractice && nextAvailableDates[nearestPractice.id]?.date || getTodayDateString()}
                          formatDateFull={formatDateFull}
                          // Find the next available date with appointments
                          nextAvailableDate={(() => {
                            // Check if we have any next available dates
                            if (Object.keys(nextAvailableDates).length > 0) {
                              // Get all dates with available slots
                              const availableDatesWithSlots = Object.values(nextAvailableDates)
                                .filter(dateInfo => dateInfo.slots && dateInfo.slots.length > 0)
                                .map(dateInfo => dateInfo.date)
                                .sort(); // Sort dates chronologically
                              
                              // Return the earliest date that's not the current date
                              const currentDate = nearestPractice && nextAvailableDates[nearestPractice.id]?.date || getTodayDateString();
                              return availableDatesWithSlots.find(date => date !== currentDate) || null;
                            }
                            return null;
                          })()}
                          onSelectNextAvailableDate={(suggestedDate) => {
                            // Check availability for the suggested date
                            const checkAvailabilityForSuggestedDate = async () => {
                              // Scroll to top of the page when selecting a suggested date
                              window.scrollTo(0, 0);
                              
                              setLoading(true);
                              try {
                                const slots = await checkAvailabilityForDate(nearestPractice.id, suggestedDate);
                                setAvailableSlots(slots);
                                setSelectedSlot(null);
                                
                                if (slots.length === 0) {
                                  setError(`No appointments available on ${formatDateFull(suggestedDate)}. Please select another date.`);
                                } else {
                                  setError(null);
                                }
                              } catch (err) {
                                console.error(`Error checking availability for date ${suggestedDate}:`, err);
                                setError(`Failed to check availability for the suggested date. Please try again.`);
                              } finally {
                                setLoading(false);
                              }
                            };
                            
                            checkAvailabilityForSuggestedDate();
                          }}
                        />
                      )}
                    </>
                  )}
                    
                  {error && !noAppointmentsWithinDistance && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                      {error}
                    </div>
                  )}
                  
                  <div className="flex flex-col md:flex-row justify-between gap-4 mt-6">
                    {availableSlots.length > 0 && (
                      <button
                        onClick={() => {
                          // Debug sortedPractices before booking
                          debugSortedPractices();
                          handleBooking();
                        }}
                        disabled={!selectedSlot || loading}
                        className={`md:flex-1 bg-accent hover:bg-accent/90 text-white font-medium py-3 px-6 rounded transition-colors ${
                          !selectedSlot || loading ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                      >
                        {loading ? 'Processing...' : 'Book Appointment'}
                      </button>
                    )}
                    
                    <button
                      onClick={handleReturnHome}
                      className={`${availableSlots.length > 0 ? 'md:w-auto' : 'w-full'} bg-gray-500 hover:bg-gray-600 text-white font-medium py-3 px-6 rounded transition-colors`}
                    >
                      {noAppointmentsWithinDistance || availableSlots.length === 0 ? 'Return to Home' : 'Cancel'}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </main>
      
      {/* Alternative Options Section */}
      <div id="alternative-options" className="py-8 bg-gray-100">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <h2 className="text-2xl font-bold text-primary font-heading mb-4">
                Alternative Options
              </h2>
              
              {!success && !showPaymentScreen && nearestPractice && Array.isArray(sortedPractices) && sortedPractices.length > 1 && (
                <div className="space-y-3">
                  <p className="text-gray-700 mb-4">
                    If the current practice doesn't suit your needs, consider these alternatives:
                  </p>
                  
                  {sortedPractices
                    .filter(p => p.id !== nearestPractice.id) // Exclude the current practice
                    .slice(0, 3) // Take up to 3 alternatives
                    .map((practice, index) => {
                      // Check if we already have appointments for this practice
                      const hasAppointments = nextAvailableDates[practice.id]?.slots?.length > 0;
                      
                      return (
                        <div key={index} className="bg-gray-50 p-4 rounded border border-gray-300 flex flex-col md:flex-row justify-between md:items-center gap-4">
                          <div>
                            <p className="font-medium text-primary text-lg">{practice.name}</p>
                            <p className="text-gray-600 text-sm mb-2">
                              {practice.address?.line1}
                              {practice.address?.line2 ? `, ${practice.address.line2}` : ''}, 
                              {practice.address?.city}, {practice.address?.postcode}
                            </p>
                            <p className="text-sm text-gray-700">
                              {nextAvailableDates[practice.id]?.slots?.[0] ? (
                                <>Next available: {new Date(nextAvailableDates[practice.id].slots[0].startTime.toDate()).toLocaleString('en-GB', {
                                  weekday: 'short',
                                  day: 'numeric',
                                  month: 'short',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}</>
                              ) : (
                                <span className="text-blue-600 hover:underline cursor-pointer" onClick={async () => {
                                  // Handle selecting an alternative practice
                                  setLoading(true);
                                  
                                  try {
                                    // Check for appointments within the next 7 days
                                    const result = await checkAppointmentsForNext7Days(practice.id);
                                    
                                    if (result) {
                                      // Found appointments
                                      const { date, slot } = result;
                                      const slots = await checkAvailabilityForDate(practice.id, date);
                                      
                                      // Update the nextAvailableDates state
                                      setNextAvailableDates(prev => ({
                                        ...prev,
                                        [practice.id]: {
                                          date,
                                          slots
                                        }
                                      }));
                                      
                                      // Update the UI
                                      setNearestPractice(practice);
                                      setAvailableSlots(slots);
                                      setSelectedSlot(null);
                                      
                                      // Scroll to top
                                      window.scrollTo({ top: 0, behavior: 'smooth' });
                                    } else {
                                      // No appointments found
                                      setError(`No available appointments found for ${practice.name} within the next 7 days.`);
                                    }
                                  } catch (err) {
                                    console.error('Error selecting alternative practice:', err);
                                    setError('Failed to check availability for the selected practice. Please try again.');
                                  } finally {
                                    setLoading(false);
                                  }
                                }}>
                                  Check availability
                                </span>
                              )}
                            </p>
                          </div>
                          <div className="flex flex-col items-start md:items-end">
                            <p className="text-sm font-medium text-accent mb-2">
                              {practice.distance ? `${practice.distance} km from your location` : 'Distance not available'}
                            </p>
                            <button 
                              className="bg-primary hover:bg-primary/90 text-white font-medium py-2 px-4 rounded transition-colors"
                              onClick={async () => {
                                // Handle selecting an alternative practice
                                setLoading(true);
                                
                                try {
                                  // If we already have appointments for this practice
                                  if (hasAppointments) {
                                    setNearestPractice(practice);
                                    setAvailableSlots(nextAvailableDates[practice.id].slots);
                                    setSelectedSlot(null);
                                  } else {
                                    // Check for appointments within the next 7 days
                                    const result = await checkAppointmentsForNext7Days(practice.id);
                                    
                                    if (result) {
                                      // Found appointments
                                      const { date, slot } = result;
                                      const slots = await checkAvailabilityForDate(practice.id, date);
                                      
                                      // Update the nextAvailableDates state
                                      setNextAvailableDates(prev => ({
                                        ...prev,
                                        [practice.id]: {
                                          date,
                                          slots
                                        }
                                      }));
                                      
                                      // Update the UI
                                      setNearestPractice(practice);
                                      setAvailableSlots(slots);
                                      setSelectedSlot(null);
                                    } else {
                                      // No appointments found
                                      setError(`No available appointments found for ${practice.name} within the next 7 days.`);
                                    }
                                  }
                                  
                                  // Scroll to top
                                  window.scrollTo({ top: 0, behavior: 'smooth' });
                                } catch (err) {
                                  console.error('Error selecting alternative practice:', err);
                                  setError('Failed to check availability for the selected practice. Please try again.');
                                } finally {
                                  setLoading(false);
                                }
                              }}
                            >
                              {hasAppointments ? 'Select This Practice' : 'Check Availability'}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
              
              {(!nearestPractice || !Array.isArray(sortedPractices) || sortedPractices.length <= 1 || success || showPaymentScreen) && (
                <p className="text-gray-700 text-center py-4">
                  No alternative options are currently available.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Treatment Pricing Section */}
      <div id="treatment-pricing" className="py-16 bg-primary">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white font-heading mb-4">
              Treatment Fees (per item)
            </h2>
            <p className="text-lg text-gray-300 max-w-3xl mx-auto">
              We won't recommend treatments you don't need, for the sake of making money. If we can fix the issue quickly and cheaply and that's the right thing for you, then we're happy to help!
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Core Treatment Card */}
            <div className="bg-primary/90 p-8 rounded-lg text-white">
              <div className="flex justify-between items-center mb-8">
                <h3 className="text-2xl font-bold font-heading">Core Treatment</h3>
                <span className="text-4xl font-bold">£49</span>
              </div>
              <div>
                <div className="flex items-center space-x-4 mb-4">
                  <div className="text-gray-400">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                  </div>
                  <span className="text-gray-200">Prescription (excluding medication)</span>
                </div>
                <div className="flex items-center space-x-4 mb-4">
                  <div className="text-gray-400">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                  </div>
                  <span className="text-gray-200">Re-cementing a Crown/Bridge</span>
                </div>
                <div className="flex items-center space-x-4 mb-4">
                  <div className="text-gray-400">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                  </div>
                  <span className="text-gray-200">Easing of dentures</span>
                </div>
                <div className="flex items-center space-x-4 mb-4">
                  <div className="text-gray-400">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                  </div>
                  <span className="text-gray-200">Semi-permanent filling</span>
                </div>
              </div>
            </div>
            
            {/* Advanced Treatment Card */}
            <div className="bg-primary/90 p-8 rounded-lg text-white">
              <div className="flex justify-between items-center mb-8">
                <h3 className="text-2xl font-bold font-heading">Advanced Treatment</h3>
                <span className="text-4xl font-bold">£149</span>
              </div>
              <div>
                <div className="flex items-center space-x-4 mb-4">
                  <div className="text-gray-400">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                  </div>
                  <span className="text-gray-200">Draining of an abscess</span>
                </div>
                <div className="flex items-center space-x-4 mb-4">
                  <div className="text-gray-400">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                  </div>
                  <span className="text-gray-200">Initial root canal treatment</span>
                </div>
                <div className="flex items-center space-x-4 mb-4">
                  <div className="text-gray-400">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                  </div>
                  <span className="text-gray-200">Extraction (excluding surgical extraction)</span>
                </div>
                <div className="flex items-center space-x-4 mb-4">
                  <div className="text-gray-400">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                  </div>
                  <span className="text-gray-200">Management of an infected socket/ wisdom tooth</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Continue with booking button */}
          <div className="text-center mt-10">
            <a 
              href="#" 
              onClick={(e) => {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className="inline-block bg-accent hover:bg-accent/90 text-white font-medium py-3 px-8 rounded transition-colors"
            >
              Continue with booking
            </a>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default AvailabilityCheckPage;
